import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useBlogPost, useUpdateBlogPost } from '@/hooks/use-blog';
import { Button } from '@/components/ui/button';
import { AdminHeader } from '@/components/admin-header';
import { PostTitleSection } from '@/components/post-title-section';
import { RichTextEditorSection } from '@/components/rich-text-editor-section';
import { ExcerptSection } from '@/components/excerpt-section';
import { SEOSection } from '@/components/seo-section';
import { PublishSettingsSection } from '@/components/publish-settings-section';
import { CategoriesTagsSection } from '@/components/categories-tags-section';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useUploadMedia } from '@/hooks/use-media';
import { MediaPicker } from '@/components/media-picker';
import { Upload, X, FolderOpen } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/blog/$id')({
  component: BlogEditPage,
});

type BlogStatus = 'draft' | 'published' | 'archived';
type BlogType = 'article' | 'video';

function BlogEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: post, isLoading, error } = useBlogPost(id);
  const updateBlogPost = useUpdateBlogPost();
  const uploadMedia = useUploadMedia();

  // Form state
  const [title, setTitle] = React.useState(post?.title || '');
  const [slug, setSlug] = React.useState(post?.slug || '');
  const [content, setContent] = React.useState(post?.content || '');
  const [description, setDescription] = React.useState(post?.description || '');
  const [excerpt, setExcerpt] = React.useState(post?.excerpt || '');
  const [status, setStatus] = React.useState<BlogStatus>(
    (post?.status as BlogStatus) ?? 'draft'
  );
  const [type, setType] = React.useState<BlogType>(
    (post?.type as BlogType) ?? 'article'
  );
  const [tags, setTags] = React.useState<string[]>(post?.tags ?? []);
  const [category, setCategory] = React.useState(post?.category || '');
  const [thumbnailUrl, setThumbnailUrl] = React.useState(post?.thumbnail_url ?? '');
  const [youtubeUrl, setYoutubeUrl] = React.useState(post?.youtube_url || '');
  const [seoTitle, setSeoTitle] = React.useState(post?.seo_title || '');
  const [seoDescription, setSeoDescription] = React.useState(post?.seo_description || '');

  // UI state
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
  const [thumbnailPickerOpen, setThumbnailPickerOpen] = React.useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = React.useState(false);

  // Helper function to detect YouTube video in content or youtubeUrl
  const hasYouTubeVideo = React.useCallback((contentText: string, youtubeUrlText: string): boolean => {
    // Check youtubeUrl field
    if (youtubeUrlText.trim()) {
      const youtubeUrlPattern = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
      if (youtubeUrlPattern.test(youtubeUrlText)) {
        return true;
      }
    }

    // Check content for YouTube embeds or links
    if (contentText.trim()) {
      const youtubePatterns = [
        /youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)([^"&?\/\s]{11})/i,
        /youtu\.be\/([^"&?\/\s]{11})/i,
        /youtube\.com\/embed\/([^"&?\/\s]{11})/i,
        /youtube\.com\/v\/([^"&?\/\s]{11})/i,
      ];
      
      for (const pattern of youtubePatterns) {
        if (pattern.test(contentText)) {
          return true;
        }
      }
    }

    return false;
  }, []);

  // Auto-detect type based on YouTube video presence
  React.useEffect(() => {
    const hasVideo = hasYouTubeVideo(content, youtubeUrl);
    setType(hasVideo ? 'video' : 'article');
  }, [content, youtubeUrl, hasYouTubeVideo]);

  // Update form state when post data loads
  React.useEffect(() => {
    if (post) {
      console.log('BlogEditPage: Setting form state from post data:', {
        id: post.id,
        title: post.title,
        hasContent: !!post.content,
        contentLength: post.content?.length || 0,
        contentPreview: post.content?.substring(0, 100),
        hasDescription: !!post.description,
        hasExcerpt: !!post.excerpt,
        tags: post.tags,
        category: post.category,
        fullPost: post,
      });

      setTitle(post.title || '');
      setSlug(post.slug || '');
      setContent(post.content || '');
      setDescription(post.description || '');
      setExcerpt(post.excerpt || '');
      setStatus((post.status as BlogStatus) ?? 'draft');
      setType((post.type as BlogType) ?? 'article');
      setTags(post.tags ?? []);
      setCategory(post.category || '');
      setThumbnailUrl(post.thumbnail_url ?? '');
      setYoutubeUrl(post.youtube_url || '');
      setSeoTitle(post.seo_title || '');
      setSeoDescription(post.seo_description || '');

      console.log('BlogEditPage: Form state set. Content:', post.content || 'EMPTY');
    } else {
      console.log('BlogEditPage: No post data available yet');
    }
  }, [post]);

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async (targetStatus?: 'draft' | 'published') => {
    // Validation
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Tiêu đề là bắt buộc';
    }
    if (!content.trim()) {
      newErrors.content = 'Nội dung là bắt buộc';
    }
    if (content.trim().length < 10) {
      newErrors.content = 'Nội dung phải có ít nhất 10 ký tự';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSaveMessage(null);

    try {
      // If targetStatus is provided, use it; otherwise keep current status
      const finalStatus = targetStatus || status;

      // Auto-detect type based on YouTube video presence
      const detectedType = hasYouTubeVideo(content.trim(), youtubeUrl.trim()) ? 'video' : 'article';

      const payload = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        content: content.trim(),
        description: description.trim() || undefined,
        excerpt: excerpt.trim() || undefined,
        status: finalStatus,
        type: detectedType,
        tags: tags.length > 0 ? tags : undefined,
        category: category.trim() || undefined,
        thumbnail_url: thumbnailUrl || undefined,
        youtube_url: detectedType === 'video' ? youtubeUrl.trim() || undefined : undefined,
        seo_title: seoTitle.trim() || undefined,
        seo_description: seoDescription.trim() || undefined,
      };

      const updatedPost = await updateBlogPost.mutateAsync({ id, payload });

      // Update local state from response to ensure consistency
      if (updatedPost) {
        if (updatedPost.status) {
          setStatus(updatedPost.status as BlogStatus);
        }
        if (updatedPost.slug) {
          setSlug(updatedPost.slug);
        }
      }

      setSaveMessage('✓ Đã lưu thành công!');
      setTimeout(() => {
        setSaveMessage(null);
        if (targetStatus === 'published') {
          navigate({ to: '/blog' });
        }
      }, 2000);
    } catch (error) {
      const err = error as Error;
      setErrors({ submit: err.message || 'Không thể lưu bài viết' });
    }
  };

  const handleSaveOnly = async () => {
    // Save without changing status - keep current status
    await handleSave();
  };

  const handleSaveAndPublish = async () => {
    await handleSave('published');
  };

  const handleUnpublish = async () => {
    // Validation
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Tiêu đề là bắt buộc';
    }
    if (!content.trim()) {
      newErrors.content = 'Nội dung là bắt buộc';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSaveMessage(null);

    try {
      // Auto-detect type based on YouTube video presence
      const detectedType = hasYouTubeVideo(content.trim(), youtubeUrl.trim()) ? 'video' : 'article';

      const payload = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        content: content.trim(),
        description: description.trim() || undefined,
        excerpt: excerpt.trim() || undefined,
        status: 'draft' as const,
        type: detectedType,
        tags: tags.length > 0 ? tags : undefined,
        category: category.trim() || undefined,
        thumbnail_url: thumbnailUrl || undefined,
        youtube_url: detectedType === 'video' ? youtubeUrl.trim() || undefined : undefined,
        seo_title: seoTitle.trim() || undefined,
        seo_description: seoDescription.trim() || undefined,
      };

      const updatedPost = await updateBlogPost.mutateAsync({ id, payload });

      // Update local state from response
      if (updatedPost) {
        if (updatedPost.status) {
          setStatus(updatedPost.status as BlogStatus);
        }
      }

      setSaveMessage('✓ Đã unpublish thành công!');
      setTimeout(() => {
        setSaveMessage(null);
      }, 2000);
    } catch (error) {
      const err = error as Error;
      setErrors({ submit: err.message || 'Không thể unpublish bài viết' });
    }
  };

  const handleSlugSave = async () => {
    // Save only the slug field
    try {
      const payload = {
        slug: slug.trim() || undefined,
      };

      await updateBlogPost.mutateAsync({ id, payload });
      setSaveMessage('✓ Đã lưu permalink thành công!');
      setTimeout(() => {
        setSaveMessage(null);
      }, 2000);
    } catch (error) {
      const err = error as Error;
      throw new Error(err.message || 'Không thể lưu permalink');
    }
  };

  const handleThumbnailUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setIsUploadingThumbnail(true);
          const result = await uploadMedia.mutateAsync({
            file,
            folder: 'blog/thumbnails',
          });
          if (result?.media?.url) {
            setThumbnailUrl(result.media.url);
          }
        } catch (error) {
          console.error('Thumbnail upload error:', error);
          setErrors({ thumbnail: 'Không thể tải lên thumbnail' });
        } finally {
          setIsUploadingThumbnail(false);
        }
      }
    };
    input.click();
  };

  // Generate permalink: use slug if exists, otherwise use generated slug from title, fallback to id
  const permalinkSlug = slug.trim() || (title
    ? title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : id);
  const postUrl = `/blog/${permalinkSlug}`;
  const permalink = postUrl;

  // Map blog status to agent moderation status for PublishSettingsSection
  const mapStatusToModerationStatus = (status: BlogStatus): 'draft' | 'pending' | 'published' | 'unpublished' | 'rejected' => {
    switch (status) {
      case 'draft':
        return 'draft';
      case 'published':
        return 'published';
      case 'archived':
        return 'unpublished';
      default:
        return 'draft';
    }
  };

  const mapModerationStatusToBlogStatus = (modStatus: 'draft' | 'pending' | 'published' | 'unpublished' | 'rejected'): BlogStatus => {
    switch (modStatus) {
      case 'draft':
      case 'pending':
        return 'draft';
      case 'published':
        return 'published';
      case 'unpublished':
      case 'rejected':
        return 'archived';
      default:
        return 'draft';
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-muted-foreground">Đang tải bài viết...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 font-medium">
            {error instanceof Error ? error.message : 'Lỗi khi tải bài viết'}
          </p>
        </div>
        <Link to="/blog">
          <Button variant="outline">Quay lại danh sách</Button>
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-600 font-medium">Bài viết không tồn tại</p>
        </div>
        <Link to="/blog">
          <Button variant="outline">Quay lại danh sách</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <AdminHeader
        breadcrumbs={[
          { label: 'Blog', href: '/blog' },
          { label: 'Chỉnh sửa Bài viết' },
        ]}
        title="Chỉnh sửa Bài viết"
        onPreview={() => window.open(postUrl, '_blank')}
        onSave={handleSaveOnly}
        onSaveAndPublish={handleSaveAndPublish}
        onUnpublish={handleUnpublish}
        isSaving={updateBlogPost.isPending}
        previewUrl={postUrl}
        isPublished={status === 'published'}
      />

      {/* Error Banner */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md mx-6 mt-6">
          <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
        </div>
      )}

      {/* Success Message */}
      {saveMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md mx-6 mt-6">
          <p className="text-sm text-green-700 font-medium">{saveMessage}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Post Title Section */}
            <PostTitleSection
              value={title}
              onChange={(value) => {
                setTitle(value);
                clearError('title');
                // Auto-generate slug from title if slug is empty
                if (!slug.trim()) {
                  const generatedSlug = value
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                  setSlug(generatedSlug);
                }
              }}
              permalink={permalink}
              code={post?.slug} // Only pass slug if it exists in DB
              onCodeChange={setSlug}
              onCodeSave={handleSlugSave}
              isCodeSaving={updateBlogPost.isPending}
              error={errors.title}
              required
              prefix="/blog/"
              label="Tiêu đề bài viết"
              placeholder="Nhập tiêu đề bài viết..."
            />

            {/* Rich Text Editor Section */}
            <RichTextEditorSection
              value={content}
              onChange={(value) => {
                setContent(value);
                clearError('content');
              }}
              placeholder="Bắt đầu viết nội dung bài viết..."
              label="Nội dung"
              error={errors.content}
              required
            />

            {/* Description Section (for video posts) */}
            {type === 'video' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả video
                  </label>
                  <Textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      clearError('description');
                    }}
                    placeholder="Nhập mô tả cho video..."
                    rows={4}
                    className={`resize-none ${
                      errors.description ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500 mt-1">{errors.description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Excerpt Section */}
            <ExcerptSection
              value={excerpt}
              onChange={setExcerpt}
              placeholder="Viết một đoạn tóm tắt ngắn về bài viết..."
              helperText="Tùy chọn. Đoạn này sẽ được sử dụng trong preview và meta description."
            />

            {/* SEO Section */}
            <SEOSection
              metaTitle={seoTitle}
              metaDescription={seoDescription}
              onMetaTitleChange={setSeoTitle}
              onMetaDescriptionChange={setSeoDescription}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <PublishSettingsSection
              status={mapStatusToModerationStatus(status)}
              visibility="public"
              onStatusChange={(modStatus) => {
                setStatus(mapModerationStatusToBlogStatus(modStatus));
              }}
              onVisibilityChange={() => {
                // Blog posts are always public
              }}
            />

            {/* Featured Image */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Hình ảnh đại diện</h3>
              </div>
              <div className="p-6">
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200 bg-gray-50"
                    />
                    <button
                      onClick={() => setThumbnailUrl('')}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                      title="Xóa hình ảnh"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={handleThumbnailUpload}
                      disabled={isUploadingThumbnail}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploadingThumbnail ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                          <p className="text-sm font-medium text-gray-600">
                            Đang tải lên...
                          </p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-4" />
                          <p className="text-sm font-medium text-gray-600 mb-2">
                            Tải lên hình ảnh đại diện
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, GIF tối đa 50MB
                          </p>
                        </>
                      )}
                    </button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setThumbnailPickerOpen(true)}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Chọn từ Media Library
                    </Button>
                  </div>
                )}
                {errors.thumbnail && (
                  <p className="text-sm text-red-500 mt-2">{errors.thumbnail}</p>
                )}
              </div>
            </div>

            {/* YouTube URL */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">YouTube URL</h3>
              </div>
              <div className="p-6">
                <Input
                  value={youtubeUrl}
                  onChange={(e) => {
                    setYoutubeUrl(e.target.value);
                    clearError('youtube_url');
                  }}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={errors.youtube_url ? 'border-red-500' : ''}
                />
                {errors.youtube_url && (
                  <p className="text-sm text-red-500 mt-1">{errors.youtube_url}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Thêm YouTube URL hoặc embed YouTube vào nội dung để tự động đánh dấu bài viết có video
                </p>
              </div>
            </div>

            {/* Categories & Tags */}
            <CategoriesTagsSection
              categories={category ? [category] : []}
              tags={tags}
              onCategoriesChange={(categories) => {
                setCategory(categories[0] || '');
              }}
              onTagsChange={setTags}
            />
          </div>
        </div>
      </div>

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={thumbnailPickerOpen}
        onClose={() => setThumbnailPickerOpen(false)}
        onSelect={(url) => {
          setThumbnailUrl(url);
          setThumbnailPickerOpen(false);
        }}
        fileType="image"
      />
    </div>
  );
}

